import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageViewerProps } from './ImageViewer.props';
import {
  ImageViewerContainer,
  ImageViewerDimensions,
  ImageViewerImage,
  ImageViewerStateContainer,
  ImageViewerWrapper,
} from './ImageViewer.styles';

const ImageViewer = ({ src, base64Content, contentType = 'image/jpeg', alt = 'Image' }: ImageViewerProps) => {
  const [imageData, setImageData] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [naturalDimensions, setNaturalDimensions] = useState<{ width: number; height: number }>();
  const imageRef = useRef<HTMLImageElement>(null);

  const maxImageWidth = 800;
  const maxImageHeight = 600;

  // Process image data
  useEffect(() => {
    const processImageData = async () => {
      setLoading(true);

      try {
        if (base64Content) {
          const dataUrl = `data:${contentType};base64,${base64Content}`;
          setImageData(dataUrl);
        } else if (src) {
          setImageData(src);
        } else {
          setError('No image source provided');
        }
      } catch (err) {
        console.error('Error processing image data:', err);
        setError('Failed to process image data');
      } finally {
        setLoading(false);
      }
    };

    processImageData();
  }, [base64Content, src, contentType]);

  // Handle image load to get natural dimensions
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      setNaturalDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      });
    }
  }, []);

  const handleImageError = useCallback(() => {
    setError('Failed to load image');
  }, []);

  if (loading) {
    return <ImageViewerStateContainer>Loading image...</ImageViewerStateContainer>;
  }

  if (error) {
    return <ImageViewerStateContainer style={{ color: 'red' }}>Error: {error}</ImageViewerStateContainer>;
  }

  if (!imageData) {
    return <ImageViewerStateContainer>No image available</ImageViewerStateContainer>;
  }

  return (
    <ImageViewerWrapper>
      <ImageViewerContainer>
        <ImageViewerImage
          ref={imageRef}
          src={imageData}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            maxWidth: maxImageWidth,
            maxHeight: maxImageHeight,
          }}
        />
      </ImageViewerContainer>

      {naturalDimensions && (
        <ImageViewerDimensions>
          {naturalDimensions.width} × {naturalDimensions.height} pixels
        </ImageViewerDimensions>
      )}
    </ImageViewerWrapper>
  );
};

export default ImageViewer;
